import { makeStyles, Text, tokens } from '@fluentui/react-components';

import type { AiToolProviderReport } from '@/api/types';
import { SectionCard } from '@/design-system';
import {
  AreaChart,
  BarChart,
  DonutChart,
  KpiTile,
  formatValue,
  type BarPoint,
  type Series,
  type Slice,
} from '@/design-system/charts';
import { spacing } from '@/theme/tokens';

const useStyles = makeStyles({
  kpis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: spacing.m,
    marginBottom: spacing.l,
  },
  charts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: spacing.l,
  },
  chartLabel: {
    display: 'block',
    marginBottom: spacing.s,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  caveats: {
    marginTop: spacing.l,
    paddingTop: spacing.m,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxs,
  },
  caveat: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

type BudgetTone = 'neutral' | 'healthy' | 'degraded' | 'unhealthy';

function budgetTone(pct: number, hasBudget: boolean): BudgetTone {
  if (!hasBudget) return 'neutral';
  if (pct > 100) return 'unhealthy';
  if (pct > 85) return 'degraded';
  return 'healthy';
}

export function ProviderSection({ provider }: { provider: AiToolProviderReport }) {
  const classes = useStyles();
  const hasBudget = provider.monthlyBudget > 0;
  const tone = budgetTone(provider.projectedBudgetConsumedPct, hasBudget);

  const dailySeries: Series[] = [
    { name: 'Daily spend', points: provider.dailySpend.map((d) => ({ t: d.dateMs, v: d.spendUsd })) },
  ];
  const modelSlices: Slice[] = provider.byModel.map((m) => ({ label: m.model, value: m.spendUsd }));
  const userBars: BarPoint[] = provider.topUsers.map((u) => ({ x: u.name, y: u.spendUsd }));

  return (
    <SectionCard
      title={provider.displayName}
      description={provider.isConfigured ? undefined : 'Not configured — no live data available.'}
    >
      <div className={classes.kpis}>
        <KpiTile
          label="Projected this month"
          value={provider.projectedMonthSpend}
          unit="usd"
          tone={tone}
          caption={hasBudget ? `Budget ${formatValue(provider.monthlyBudget, 'usd')}` : 'No budget set'}
        />
        <KpiTile label="Month to date" value={provider.monthToDateSpend} unit="usd" />
        <KpiTile
          label="Budget used"
          value={hasBudget ? provider.budgetConsumedPct : null}
          unit="percent"
          tone={tone}
          caption={hasBudget ? `Projected ${formatValue(provider.projectedBudgetConsumedPct, 'percent')}` : undefined}
        />
        <KpiTile
          label="Budget days left"
          value={provider.budgetDaysRemaining ?? null}
          caption="at current daily burn"
        />
        {provider.seats ? (
          <KpiTile
            label="Seats used"
            value={provider.seats.used}
            caption={`of ${String(provider.seats.total)} purchased`}
          />
        ) : null}
      </div>

      <div className={classes.charts}>
        <div>
          <Text className={classes.chartLabel} size={200}>
            Daily spend
          </Text>
          {provider.dailySpend.length > 0 ? (
            <AreaChart series={dailySeries} unit="usd" tone="brand" height={200} />
          ) : (
            <Text className={classes.empty}>No spend recorded in this window.</Text>
          )}
        </div>

        <div>
          <Text className={classes.chartLabel} size={200}>
            Spend by model
          </Text>
          {modelSlices.length > 0 ? (
            <DonutChart slices={modelSlices} unit="usd" />
          ) : (
            <Text className={classes.empty}>No per-model breakdown available.</Text>
          )}
        </div>

        <div>
          <Text className={classes.chartLabel} size={200}>
            Top users by spend
          </Text>
          {userBars.length > 0 ? (
            <BarChart bars={userBars} unit="usd" tone="brand" height={200} />
          ) : (
            <Text className={classes.empty}>Per-user spend isn’t available for this provider.</Text>
          )}
        </div>
      </div>

      {provider.caveats.length > 0 ? (
        <div className={classes.caveats}>
          {provider.caveats.map((c, idx) => (
            <Text key={`${provider.providerKey}-${String(idx)}`} className={classes.caveat}>
              • {c}
            </Text>
          ))}
        </div>
      ) : null}
    </SectionCard>
  );
}
